import React, { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X, MapPin, BarChart3, CloudSun, Calendar, Search, ChevronRight } from "lucide-react";
import type { DiscoveryVariant } from "./useSearchDiscovery";
import "./SearchDiscoveryTooltip.css";

const EXAMPLE_QUERY = "KOTPUTLI humidity next week";

const TOOLTIP_WIDTH = 300;
const MARGIN = 12;
const EXIT_MS = 150;

const CAPABILITIES = [
  { icon: <MapPin size={15} className="sdt-icon-map" aria-hidden="true" />, label: "Feeders &\nSubstations", bgClass: "sdt-bg-map" },
  { icon: <BarChart3 size={15} className="sdt-icon-chart" aria-hidden="true" />, label: "Metrics &\nRankings", bgClass: "sdt-bg-chart" },
  { icon: <CloudSun size={15} className="sdt-icon-weather" aria-hidden="true" />, label: "Weather\nAnalysis", bgClass: "sdt-bg-weather" },
  { icon: <Calendar size={15} className="sdt-icon-calendar" aria-hidden="true" />, label: "Date\nQueries", bgClass: "sdt-bg-calendar" },
];

export interface SearchDiscoveryTooltipProps {
  anchorRef: React.RefObject<HTMLElement>;
  variant: DiscoveryVariant;
  onTryExample: (query: string) => void;
  onComplete: () => void;
  /** ESC → suppress for the rest of the page session (non-completing). */
  onSuppress: () => void;
  /** Scroll → soft hide (re-armable this session, non-completing). */
  onHideSoft: () => void;
  /** Pointer entered the tooltip — cancel the pointer-leave hide. */
  onPointerEnter: () => void;
  /** Pointer left the tooltip — start the pointer-leave hide grace. */
  onPointerLeave: () => void;
}

export default function SearchDiscoveryTooltip({
  anchorRef,
  variant,
  onTryExample,
  onComplete,
  onSuppress,
  onHideSoft,
  onPointerEnter,
  onPointerLeave,
}: SearchDiscoveryTooltipProps) {
  const [pos, setPos] = useState<{ top: number; left: number; beak: number } | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);

  useLayoutEffect(() => {
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      let left = r.left;
      const maxLeft = window.innerWidth - TOOLTIP_WIDTH - MARGIN;
      left = Math.max(MARGIN, Math.min(left, maxLeft));
      const beak = Math.max(12, Math.min(r.left + 22 - left, TOOLTIP_WIDTH - 18));
      setPos({ top: r.bottom + 10, left, beak });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [anchorRef]);

  const leaveThen = useCallback(
    (cb: () => void) => {
      setIsLeaving(true);
      setTimeout(cb, EXIT_MS);
    },
    [],
  );

  // Play the exit fade, then run the given hide callback once.
  const fadeOutThen = useCallback((cb: () => void) => {
    setIsLeaving((leaving) => {
      if (leaving) return leaving;
      setTimeout(cb, EXIT_MS);
      return true;
    });
  }, []);

  const handleSuppress = useCallback(
    () => fadeOutThen(onSuppress),
    [fadeOutThen, onSuppress],
  );
  const handleHideSoft = useCallback(
    () => fadeOutThen(onHideSoft),
    [fadeOutThen, onHideSoft],
  );

  const handleComplete = useCallback(() => {
    if (isLeaving) return;
    leaveThen(onComplete);
  }, [isLeaving, leaveThen, onComplete]);

  const handleTryExample = useCallback(() => {
    if (isLeaving) return;
    leaveThen(() => onTryExample(EXAMPLE_QUERY));
  }, [isLeaving, leaveThen, onTryExample]);

  // ESC → suppress for the session (does NOT complete; not re-armable this session).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleSuppress();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleSuppress]);

  // Scroll → soft hide (non-completing, re-armable on a later deliberate hover).
  useEffect(() => {
    const onScroll = () => handleHideSoft();
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [handleHideSoft]);

  if (!pos) return null;

  const style = {
    top: pos.top,
    left: pos.left,
    ["--sdt-beak" as string]: `${pos.beak}px`,
  } as React.CSSProperties;

  const shellClass = `search-discovery-tooltip${variant === "compact" ? " is-compact" : ""}${isLeaving ? " is-leaving" : ""}`;

  // Pointer over the tooltip is part of the search region: entering cancels the
  // 2s leave-hide, leaving (re)starts it.
  const regionHandlers = {
    onMouseEnter: onPointerEnter,
    onMouseLeave: onPointerLeave,
  };

  if (variant === "compact") {
    return createPortal(
      <div
        className={shellClass}
        role="dialog"
        aria-label="Search tips"
        style={style}
        {...regionHandlers}
      >
        <Sparkles size={14} aria-hidden="true" className="sdt-compact-icon" />
        <span className="sdt-compact-text">
          Search supports weather, metrics and date queries
        </span>
        <button
          className="sdt-compact-close"
          onClick={handleComplete}
          aria-label="Dismiss search tips"
        >
          <X size={13} />
        </button>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className={shellClass}
      role="dialog"
      aria-label="Search tips"
      style={style}
      {...regionHandlers}
    >
      <button className="sdt-close" onClick={handleComplete} aria-label="Dismiss search tips">
        <X size={14} />
      </button>

      <div className="sdt-header">
        <Sparkles size={16} aria-hidden="true" className="sdt-header-icon" />
        <span className="sdt-title-dark">Search </span>
        <span className="sdt-title-orange">Anything</span>
      </div>

      <p className="sdt-desc">
        Find pages, feeders, metrics, weather insights and date-based analysis.
      </p>

      <div className="sdt-caps-grid">
        {CAPABILITIES.map((cap, i) => (
          <React.Fragment key={cap.label}>
            <div className="sdt-cap-col">
              <div className={`sdt-cap-icon-circle ${cap.bgClass}`}>
                {cap.icon}
              </div>
              <div className="sdt-cap-label">
                {cap.label.split("\n").map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
              </div>
            </div>
            {i < CAPABILITIES.length - 1 && <div className="sdt-cap-divider" />}
          </React.Fragment>
        ))}
      </div>

      <div className="sdt-example-box">
        <div className="sdt-example-label">Example search</div>
        <button
          className="sdt-example-chip"
          onClick={handleTryExample}
          aria-label={`Try example search: ${EXAMPLE_QUERY}`}
        >
          <Search size={13} className="sdt-chip-icon-left" />
          <span className="sdt-chip-text">{EXAMPLE_QUERY}</span>
          <ChevronRight size={13} className="sdt-chip-icon-right" />
        </button>
      </div>
    </div>,
    document.body,
  );
}
